// Playground people with no Salesforce contact, as contacts the push and the builder can tick.
// Pure, so `node --test` covers it. The server resolves the same `playground:<address>` key,
// saves the person to Salesforce before a real send, and withholds them if that save is refused
// (app.py `_withheld_reason`).
import type { BookAccount, Contact, Hero } from '../api/types.ts';
import { cleanName, hasUsableName } from './contactName.ts';

/** The account's playground entry for an address, which carries any mirrored suppression. */
function playgroundPerson(row: BookAccount | null, email: string) {
  return (row?.playground_people ?? []).find(person => person.email.toLowerCase() === email.toLowerCase()) ?? null;
}

/**
 * A playground hero with no contact, as a contact keyed by their hero key. Null for anyone
 * else, or for a playground hero with no address. A suppression flag is true when a mirrored
 * contact with the address carries it, else unknown (null).
 */
export function playgroundContact(hero: Hero, row: BookAccount | null = null): Contact | null {
  if (hero.contact_id || hero.source !== 'playground' || !hero.email) return null;
  const person = playgroundPerson(row, hero.email);
  return {
    id: hero.key, name: cleanName(hero.name), title: hero.title, department: null, email: hero.email, phone: null,
    linkedin: hero.linkedin, last_activity: null, orgs: null, paid_orgs: null,
    opted_out: person?.opted_out ?? null, bounced: person?.bounced ?? null,
    amplemarket_excluded: person?.amplemarket_excluded ?? null,
    sequence: null, gong_flow: null, gong_flow_status: null, playground_person: true,
  };
}

/** Every playground person on the account who can be addressed, in hero order. */
export function playgroundContacts(row: BookAccount): Contact[] {
  return (row.heroes ?? []).flatMap(hero => {
    const contact = playgroundContact(hero, row);
    return contact ? [contact] : [];
  });
}

/** Everyone the push and the builder may name: Salesforce contacts, then playground people. */
export function sendableContacts(row: BookAccount): Contact[] {
  return [...row.contacts, ...playgroundContacts(row)];
}

/** Why a playground hero has no checkbox at all: there is no address to send to. */
export function playgroundUntickableReason(hero: Hero | null): string | null {
  if (!hero || hero.contact_id || hero.source !== 'playground' || hero.email) return null;
  return 'No email address on record for this playground user.';
}

/**
 * Why a playground person cannot be ticked yet: Salesforce cannot save someone with no name. A
 * name read off an address counts only on the strict rule the server's save uses
 * (contact_names.py): `bogdan.banu@` yes, `growth.team@` or `voice.ai@` no.
 */
export function playgroundNeedsName(contact: Contact): boolean {
  return Boolean(contact.playground_person) && !hasUsableName(contact, true);
}
